import { Component, computed, inject, OnInit, signal, viewChild, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatSliderModule } from '@angular/material/slider';
import { ApplicationApiService, RoadmapSearchResultDto } from 'src/app/services/application-api.service';
import { LoadingComponent } from 'src/app/components/loading/loading.component';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatTableModule,
    MatSortModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSliderModule,
    LoadingComponent,
    MatButtonModule,
    MatProgressBarModule
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss'
})
export class SearchComponent implements OnInit {
  private readonly apiService = inject(ApplicationApiService);
  isLoading = signal<boolean>(false);
  isSearching = signal<boolean>(false);
  hasSearched = signal<boolean>(false);
  marketSegments = signal<string[]>([]);
  searchResults = signal<RoadmapSearchResultDto[]>([]);
  dataSource = new MatTableDataSource<RoadmapSearchResultDto>([]);
  displayedColumns: string[] = ['projectName', 'projectNumber', 'version', 'status', 'profitCenterName', 'marketSegment', 'revenue', 'isApproved', 'leanChampionName', 'lastModifiedOn'];

  readonly sort = viewChild(MatSort);

  constructor() {
    effect(() => {
      const sortRef = this.sort();
      if (sortRef) {
        this.dataSource.sort = sortRef;
        this.dataSource.sortingDataAccessor = (item: RoadmapSearchResultDto, property: string) => {
          switch (property) {
            case 'status': return item.status?.name || '';
            case 'profitCenterName': return item.profitCenter?.profitCenterName || '';
            default: return (item as any)[property];
          }
        };
      }
    });
  }

  // Search form fields
  projectName = signal<string>('');
  jobNumber = signal<string>('');
  selectedMarketSegment = signal<string>('');
  volumeMin = signal<number>(0);
  volumeMax = signal<number>(1000000000);

  // Computed signal to check if any field has changed from defaults
  isSearchDisabled = computed(() =>
    this.projectName() === '' &&
    this.jobNumber() === '' &&
    this.selectedMarketSegment() === '' &&
    this.volumeMin() === 0 &&
    this.volumeMax() === 1000000000
  );

  ngOnInit(): void {
    this.isLoading.set(true);
    this.apiService.apiProjectsMarketSegments().subscribe((segments: string[]) => {
      this.marketSegments.set(segments);
      this.isLoading.set(false);
    });
  }

  onSearchClick(): void {
    this.isSearching.set(true);
    this.apiService.apiRoadmapSearch(
      this.projectName().length === 0 ? undefined : this.projectName(),
      this.jobNumber().length === 0 ? undefined : this.jobNumber(),
      this.volumeMin(),
      this.volumeMax() === 1000000000 ? undefined : this.volumeMax(),
      this.selectedMarketSegment()
    ).subscribe((results: RoadmapSearchResultDto[]) => {
      this.searchResults.set(results);
      this.dataSource.data = results;
      this.isSearching.set(false);
      this.hasSearched.set(true);
    });
  }

  onRoadmapClick(row: RoadmapSearchResultDto): void {
    const id = row.id;
    const version = row.version;
    //open in new tab
    window.open(`/roadmap/${id}/${version}`, '_blank');
  }
}
